import { Box, Typography } from '@mui/material';
import React from 'react';
import type { Distribution, Fund } from '../../data/types';
import { distributionTotals } from '../../domain/distributionCalc';
import { useTenantScope } from '../../store/selectors';
import { KeyValueGrid, SectionCard } from '../../ui/primitives';

const pad = (value: string, length: number) => value.slice(0, length).padEnd(length, ' ');
const cents = (value: number, length: number) =>
  String(Math.round(value * 100)).padStart(length, '0');

export default function AbaFilePreview({
  distribution,
  fund,
}: {
  distribution: Distribution;
  fund: Fund;
}) {
  const scope = useTenantScope();
  const cashLines = distribution.lines.filter((line) => line.method === 'CASH' && line.net > 0);
  const totals = distributionTotals(distribution);
  const header = `0                 01WBC       ${pad(fund.bank.accountName.toUpperCase(), 26)}${fund.apcaUserId}${pad('DISTRIBUTN', 12)}${distribution.paymentDate.slice(8, 10)}${distribution.paymentDate.slice(5, 7)}${distribution.paymentDate.slice(2, 4)}`;
  const detail = cashLines.slice(0, 6).map((line) => {
    const holder = scope.holderById.get(line.holderId);
    return `1${holder?.bank.bsb ?? '000-000'}${pad(holder?.bank.accountNumber ?? '', 9)} 50${cents(line.net, 10)}${pad((holder?.name ?? '').toUpperCase(), 32)}${pad(`${fund.code} DIST ${distribution.periodEnd.slice(0, 7)}`, 18)}${fund.bank.bsb}${pad(fund.bank.accountNumber, 9)}`;
  });
  const footer = `7999-999            ${cents(totals.cash, 10)}${cents(totals.cash, 10)}${cents(0, 10)}                        ${String(cashLines.length).padStart(6, '0')}`;
  return (
    <SectionCard
      title="Bank file (ABA)"
      subtitle={distribution.abaFileName ?? 'Generated on approval'}
    >
      <KeyValueGrid
        columns={3}
        items={[
          { label: 'Payer name', value: fund.bank.accountName },
          { label: 'APCA user ID', value: fund.apcaUserId },
          { label: 'Reference', value: `${fund.code} DIST ${distribution.periodEnd.slice(0, 7)}` },
        ]}
      />
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1, mb: 1.5 }}>
        Taken from the fund record set up once, so nothing is re-keyed for each payment.
      </Typography>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.5,
          bgcolor: '#0F172A',
          color: '#E2E8F0',
          borderRadius: 1.5,
          fontSize: '0.7rem',
          overflowX: 'auto',
          lineHeight: 1.6,
        }}
      >
        {[
          header,
          ...detail,
          cashLines.length > 6 ? `… ${cashLines.length - 6} more payment records` : '',
          footer,
        ]
          .filter(Boolean)
          .join('\n')}
      </Box>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
        {cashLines.length} cash payments. DRP holders receive units instead; holders with unverified
        bank accounts are held back automatically.
      </Typography>
    </SectionCard>
  );
}
